import { useState, useEffect } from 'react';
import { mediaUrl } from '../utils/media';
import { useParams } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import StarRating from '../components/catalog/StarRating';
import PaymentModal from '../components/checkout/PaymentModal';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

export default function StorePage() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [ebooks, setEbooks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [cart, setCart] = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [buying, setBuying] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const user = getUser();

  useEffect(() => {
    api.get(`/api/store/${slug}`)
      .then(r => { setStore(r.data); setEbooks(r.data.ebooks || []); })
      .catch(() => toast.error('Toko tidak ditemukan'))
      .finally(() => setLoading(false));
  }, [slug]);

  const addToCart = (ebook) => {
    if (cart.find(c => c.id === ebook.id)) { toast('Sudah di keranjang'); return; }
    setCart(p => [...p, ebook]);
    toast.success('Ditambah ke keranjang');
  };

  const handleBuy = () => {
    if (!user) { navigate('/login'); return; }
    setShowSummary(true);
  };

  const confirmBuy = async (voucherCode) => {
    setShowSummary(false);
    setBuying(true);
    try {
      const res = await api.post('/api/orders', { ebook_id: selected.id, voucher_code: voucherCode });
      setActiveOrder(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal membuat order');
    } finally {
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-screen bg-wa-bg">
        <Navbar title={store?.tenant?.name || 'Toko'} subtitle={store?.tenant?.description} />
        <div className="flex-1 flex items-center justify-center text-gray-400">Memuat toko...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-wa-bg pb-16 md:pb-0">
      <Navbar title={store?.tenant?.name || 'Toko'} subtitle={store?.tenant?.description} />

      {/* Store header */}
      <div className="bg-wa-dark px-6 py-8 text-white">
        <h1 className="text-2xl font-bold">{store?.name || slug}</h1>
        {store?.description && <p className="text-green-100 mt-1 text-sm">{store.description}</p>}
      </div>

      {selected ? (
        /* Detail panel */
        <div className="flex-1 p-4 max-w-lg mx-auto w-full">
          <button onClick={() => setSelected(null)} className="text-wa-dark text-sm mb-4 flex items-center gap-1">
            ← Kembali ke Toko
          </button>
          {selected.cover_url ? (
            <img src={mediaUrl(selected.cover_url)} alt={selected.title} className="w-full h-48 object-cover rounded-xl mb-4" />
          ) : (
            <div className="w-full h-48 bg-wa-light rounded-xl mb-4 flex items-center justify-center">
              <BookOpenIcon className="w-16 h-16 text-wa-dark" />
            </div>
          )}
          <h2 className="text-xl font-bold text-gray-800 mb-1">{selected.title}</h2>
          <p className="text-gray-500 text-sm mb-2">{selected.seller_name || store?.name || 'Penjual'}</p>
          <StarRating rating={selected.avg_rating} count={selected.review_count} />
          <div className="flex items-center gap-2 mt-2 mb-3 flex-wrap">
            {selected.discount_pct > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">-{selected.discount_pct}%</span>
            )}
            {selected.discount_pct > 0 ? (
              <>
                <p className="text-2xl font-bold text-wa-dark">
                  Rp {Number(selected.final_price || selected.price).toLocaleString('id-ID')}
                </p>
                <p className="text-gray-400 line-through text-sm">
                  Rp {Number(selected.price).toLocaleString('id-ID')}
                </p>
              </>
            ) : (
              <p className="text-2xl font-bold text-wa-dark">
                {selected.price === 0 ? 'Gratis' : `Rp ${Number(selected.price).toLocaleString('id-ID')}`}
              </p>
            )}
          </div>
          {selected.description && (
            <p className="text-gray-600 text-sm mb-4 leading-relaxed">{selected.description}</p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => addToCart(selected)}
              className="flex-1 border border-wa-dark text-wa-dark py-2.5 rounded-full font-medium hover:bg-wa-light transition"
            >
              + Keranjang
            </button>
            <button
              onClick={handleBuy}
              disabled={buying}
              className="flex-1 bg-wa-dark text-white py-2.5 rounded-full font-medium hover:bg-green-900 transition disabled:opacity-50"
            >
              {buying ? 'Memproses...' : 'Beli Sekarang'}
            </button>
          </div>
        </div>
      ) : (
        /* Catalog grid */
        <div className="p-4">
          {ebooks.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Belum ada ebook di toko ini.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {ebooks.map(ebook => (
                <div
                  key={ebook.id}
                  onClick={() => setSelected(ebook)}
                  className="bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition"
                >
                  {ebook.cover_url ? (
                    <img src={mediaUrl(ebook.cover_url)} alt={ebook.title} className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-32 bg-wa-light flex items-center justify-center">
                      <BookOpenIcon className="w-10 h-10 text-wa-dark" />
                    </div>
                  )}
                  <div className="p-2">
                    <p className="text-gray-800 text-xs font-medium truncate">{ebook.title}</p>
                    <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                      {ebook.discount_pct > 0 && (
                        <span className="bg-red-500 text-white text-[10px] font-bold px-1 py-0.5 rounded">-{ebook.discount_pct}%</span>
                      )}
                      {ebook.discount_pct > 0 ? (
                        <>
                          <span className="text-wa-dark text-xs font-semibold">Rp {Number(ebook.final_price || ebook.price).toLocaleString('id-ID')}</span>
                          <span className="text-gray-400 line-through text-[10px]">Rp {Number(ebook.price).toLocaleString('id-ID')}</span>
                        </>
                      ) : (
                        <span className="text-wa-dark text-xs font-semibold">
                          {ebook.price === 0 ? 'Gratis' : `Rp ${Number(ebook.price).toLocaleString('id-ID')}`}
                        </span>
                      )}
                    </div>
                    <StarRating rating={ebook.avg_rating} count={ebook.review_count} />
                    <button
                      onClick={e => { e.stopPropagation(); addToCart(ebook); }}
                      className="mt-1.5 w-full text-xs border border-wa-dark text-wa-dark py-1 rounded-full hover:bg-wa-light transition"
                    >
                      + Keranjang
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(showSummary || activeOrder) && selected && (
        <PaymentModal
          order={activeOrder || { id: null, ebook_title: selected.title, amount: selected.price, status: 'pending' }}
          ebook={selected}
          user={user}
          buying={buying}
          onConfirm={confirmBuy}
          onClose={() => { setShowSummary(false); setActiveOrder(null); }}
          onSuccess={() => { setShowSummary(false); setActiveOrder(null); navigate('/library'); }}
        />
      )}
    </div>
  );
}
